import {
    PropsWithChildren,
    ReactNode,
    RefObject,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import ReactDOM from 'react-dom';
import {
    BodyContent,
    RootSection,
    ActionContent,
    TitleContent,
    SHEET_EXIT_MS,
    type PageMove,
} from './modal.styles';
import { Group } from '../group';
import { Composite } from '../composite';
import { useLockBodyScroll } from '../../utils/useLockBodyScroll';
import { isBrowser, isStorybook } from '../../utils/env';
import { IconButton } from '../../io/icon-button';
import { Size, Variant } from '../../types';
import { CloseIcon } from '../../themes/base/icons/named';

export type ModalProps = PropsWithChildren<{
    titleContent?: ReactNode;
    showModal: boolean;
    onClosePress?: () => void;
    maxWidth?: string;
    width?: string;
    padding?: string;
    zIndex?: number;
    titleGap?: number;
    titleDivider?: boolean;
    /** The close button's accessible name: it shows only an icon. Translate it with the app. */
    closeLabel?: string;
    /**
     * The dialog's accessible name, for a modal without `titleContent` (or whose title does not
     * name it well). Otherwise the dialog is named by its `titleContent`.
     */
    'aria-label'?: string;
    /**
     * Whether it animates in as it shows (default true). False for a modal that is already open
     * as the page arrives — a link or a reload into it — where there is no opening to show.
     * Closing animates either way.
     */
    appear?: boolean;
    /**
     * Which of the modal's pages shows, for a modal that moves between several (the steps of a
     * flow): when `key` changes, the new page arrives from `direction`'s side — sliding in on a
     * phone, fading in on a desktop. `forward` (the default) for a step on, `back` for a step back.
     */
    page?: { key: string; direction?: 'forward' | 'back' };
}>;

export function Modal({
    titleContent,
    children,
    showModal = false,
    onClosePress,
    maxWidth,
    width,
    padding,
    zIndex,
    titleGap = 0,
    titleDivider = false,
    closeLabel = 'Close',
    'aria-label': ariaLabel,
    appear = true,
    page,
}: ModalProps) {
    const titleId = useId();

    // The last move to another page; none for the page the modal opened on.
    const [pageKey, setPageKey] = useState(page?.key);
    const [pageMove, setPageMove] = useState<PageMove | null>(null);
    if (page?.key !== pageKey) {
        setPageKey(page?.key);
        setPageMove({
            direction: page?.direction ?? 'forward',
            turn: pageMove?.turn === 0 ? 1 : 0,
        });
    }

    /*
     * Closing plays the opening backwards: a dialog shrinks and fades, a phone's sheet slides down,
     * as the backdrop fades. The modal stays for that moment, showing what it showed last: an app
     * that empties the modal the moment it closes (content behind `open &&`) would otherwise see
     * an empty dialog go. Not under reduced motion, where it goes at once.
     */
    const [wasShown, setWasShown] = useState(showModal);
    const [leaving, setLeaving] = useState(false);
    const [last, setLast] = useState({ titleContent, children });
    if (showModal !== wasShown) {
        setWasShown(showModal);
        setLeaving(!showModal && exitAnimates());
    }
    if (showModal && (last.children !== children || last.titleContent !== titleContent)) {
        setLast({ titleContent, children });
    }
    useEffect(() => {
        if (!leaving) return;
        const timer = setTimeout(() => setLeaving(false), SHEET_EXIT_MS);
        return () => clearTimeout(timer);
    }, [leaving]);
    const shownTitle = leaving ? last.titleContent : titleContent;
    const shownChildren = leaving ? last.children : children;

    useEffect(() => {
        const close = (e: KeyboardEvent) => {
            /*
             * Something nearer the key press may have already used it — a picker dismissing its
             * open option list, say. Closing the dialog on top of that discards whatever has been
             * typed into it, for a keystroke the reader meant only for the popup.
             */
            if (e.defaultPrevented) {
                return;
            }
            if (onClosePress && e.key === 'Escape') {
                onClosePress();
            }
        };
        window.addEventListener('keydown', close);
        return () => window.removeEventListener('keydown', close);
    }, [onClosePress]);

    useLockBodyScroll(isBrowser && showModal);
    const rootRef = useRef<HTMLElement>(null);
    useFollowVisualViewport(rootRef, showModal || leaving);
    const portalTarget = useMemo(() => getOrCreateModalContainer(), []);

    /*
     * Closed, and done animating away: nothing at all. A closed modal kept hidden still rendered
     * itself and everything in it on each render of the page, for nobody to see; a page with a few
     * of them paid for all of them (and carried hidden dialogs). It opens afresh: what it showed,
     * a half-filled form say, doesn't keep from one opening to the next.
     */
    if (!showModal && !leaving) {
        return null;
    }

    const modalContent = (
        <RootSection
            ref={rootRef}
            onClick={onClosePress}
            width={width}
            $zIndex={zIndex}
            $padding={padding}
            $showModal={showModal || leaving}
            $leaving={leaving}
            $appear={appear}
            $pageMove={pageMove}
            $maxWidth={maxWidth}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            aria-labelledby={!ariaLabel && shownTitle ? titleId : undefined}
        >
            <Group stopPropagation>
                {shownTitle ? (
                    <Composite divider={titleDivider} gap={titleGap} noWrap>
                        <TitleContent id={titleId}>{shownTitle}</TitleContent>
                        {onClosePress && (
                            <IconButton
                                icon={CloseIcon}
                                variant={Variant.GRAYSCALE}
                                aria-label={closeLabel}
                                onClick={onClosePress}
                                size={Size.SMALL}
                            />
                        )}
                    </Composite>
                ) : undefined}
                {shownChildren}
            </Group>
        </RootSection>
    );
    if (!isBrowser) {
        return null;
    }
    if (isStorybook) {
        // In Storybook, render inline so it doesn't block the docs or UI
        return <div>{modalContent}</div>;
    } else if (portalTarget) {
        // In your normal app, render in a portal
        return ReactDOM.createPortal(modalContent, portalTarget);
    }

    return null;
}

export const ModalBodyContent = BodyContent;
export const ModalActionContent = ActionContent;

/*
 * Places the modal where the screen is showing the page. On a phone, a page wider than the screen is
 * laid out wider than it, and the reader pans across that layout; position: fixed stays put in the
 * layout, so a modal opened on a panned or zoomed page, or panned away from, would sit off to the
 * side. The visual viewport is what the screen shows; its offset and size go to the modal as
 * --modal-viewport-* (see RootSection), and follow it while the modal is open. Where the screen
 * shows the whole layout (a desktop, a responsive page), they are the layout's, and nothing moves.
 */
function useFollowVisualViewport(ref: RefObject<HTMLElement | null>, active: boolean) {
    useEffect(() => {
        const viewport = isBrowser ? window.visualViewport : null;
        const element = ref.current;
        if (!active || !viewport || !element) {
            return;
        }
        const follow = () => {
            element.style.setProperty('--modal-viewport-left', `${viewport.offsetLeft}px`);
            element.style.setProperty('--modal-viewport-top', `${viewport.offsetTop}px`);
            element.style.setProperty('--modal-viewport-width', `${viewport.width}px`);
            element.style.setProperty('--modal-viewport-height', `${viewport.height}px`);
        };
        follow();
        viewport.addEventListener('resize', follow);
        viewport.addEventListener('scroll', follow);
        return () => {
            viewport.removeEventListener('resize', follow);
            viewport.removeEventListener('scroll', follow);
        };
    }, [ref, active]);
}

/** Whether a closing modal animates its exit: whenever motion is allowed. */
function exitAnimates() {
    return (
        isBrowser &&
        // Not in every environment (jsdom has none): then nothing animates.
        typeof window.matchMedia === 'function' &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
}

function getOrCreateModalContainer(): Element | null {
    if (!isBrowser) return null;
    let container = document.getElementById('modal-root');
    if (!container) {
        container = document.createElement('div');
        container.setAttribute('id', 'modal-root');
        document.body.appendChild(container);
    }
    return container;
}
